import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import axios from "axios";
const API_URL = process.env.REACT_APP_BASE_URL;
const headers = {
  "Content-Type": "application/json",
};

export interface Machinary {
  id?: number;
  packageName: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}
export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
export const fetchMachinaryList = createAsyncThunk(
  "machinary/fetchMachinaryList",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.id && filter.value !== undefined && filter.value !== "") {
            params[filter.id] = filter.value;
          }
        });
      }

      const response: any = await Axios.get(`/machinarys/`, { params });
      const responseData = response.data;
      return {
        data: responseData.data || responseData.results || [],
        rowCount: responseData.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchMachinaryList error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch machinary list");
    }
  }
);

export const fetchMachinarys = createAsyncThunk(
  "machinary/fetchMachinary",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      if (sort) {
        params.ordering = order === "desc" ? `-${sort}` : sort;
      }

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.id && filter.value !== undefined && filter.value !== "") {
            params[filter.id] = filter.value;
          }
        });
      }

      const response: any = await Axios.get(`/machinarys/`, { params });

      const responseData = response.data;
      return {
        data: responseData.data || responseData.results || [],
        rowCount: responseData.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchMachinary error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch organisations");
    }
  }
);

export const fetchMachinarysById = createAsyncThunk(
  "machinary/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/machinarys${id}/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch fetchMachinarysById by ID"
      );
    }
  }
);

export const updateMachinary = createAsyncThunk(
  `/organisation/updateMachinary`,
  async (packageData: any) => {
    const response: any = await Axios.put(
      `${API_URL}/machinary/${packageData.id}/`,
      packageData
    );

    return response;
  }
);
export const updateStatusInMachinary = createAsyncThunk(
  `/machinary/updateStatusInMachinary`,
  async (machinarysData: any) => {
    const response: any = await Axios.put(
      `/machinarys/${machinarysData?.machinary_id}/update/`,
      machinarysData
    );
    return response;
  }
);

export const addMachinary = createAsyncThunk(
  `/machinary/addMachinary`,
  async (packageData: any) => {
    const response = await Axios.post(`${API_URL}organisation/`, packageData);
    return response.data;
  }
);

export const deleteOrganisation = createAsyncThunk(
  `/machinary/delete`,
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      return id;
    } catch (err: any) {
      console.error("Delete error:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const exportMachinarysCSV = createAsyncThunk(
  "machinarys/exportOMachinarysCSV",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getMachinary("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };
      const response = await Axios.get("/machinarys/?export=csv/", {
        headers,
        responseType: "blob",
      });
      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "machinarys.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  }
);

export const fetchAllDropdowns = createAsyncThunk(
  "machinary/fetchAllDropdowns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/machinary/dropdown/`);

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch package list"
      );
    }
  }
);

export const fetchAllDropdownsMachinarys = createAsyncThunk(
  "machinarys/fetchAllDropdownsMachinarys",
  async () => {
    try {
      const [orgRes, machinarysRes, departmentRes] = await Promise.all([
        Axios.get(`${API_URL}/organization/all-org/`, { headers }),
        Axios.get(`${API_URL}/machinarys/get-all/`, { headers }),
        Axios.get(`${API_URL}/department/get-all/`, { headers }),
      ]);

      return {
        orgDropDown: Array.isArray(orgRes.data.data)
          ? orgRes.data.data.map(
              (machinary: any) => machinary.organization_name
            )
          : [],
        departmentDropDown: Array.isArray(departmentRes.data.data)
          ? departmentRes.data.data.map(
              (machinary: any) => machinary.department_name
            )
          : [],
        machinarysDropDown: Array.isArray(machinarysRes.data.data)
          ? machinarysRes.data.data.map(
              (machinary: any) => machinary.machinary_name
            )
          : [],
      };
    } catch (err) {
      throw err;
    }
  }
);
